import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import isTokenExpired from '@/utils/token';
import { refreshAccessToken } from '@/services/refreshService';
import useUser from '@/hooks/useUser';
import { getAccessToken, getRefreshToken, removeTokens, saveTokens } from '@/utils/tokenStorage';
import { useNavigate } from 'react-router';
import { routes } from '@/constants/routes';
import { appEvents, EXPIRED_SESSION_EVENT } from '@/utils/appEvents';
import { enqueueSnackbar } from 'notistack';

interface AuthContextType {
  token: string | null;
  isLoading: boolean;
  login: (token: string, refreshToken: string, userData?: any) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  token: null,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
  refreshAccessToken: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { loadUserData } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    const loadToken = async () => {
      try {
        const savedToken = getAccessToken();
        const savedRefreshToken = getRefreshToken();

        if (savedToken && !isTokenExpired(savedToken)) {
          setToken(savedToken);
          await loadUserData(savedToken);
        } else if (savedRefreshToken) {
          await handleRefreshToken();
        } else {
          await logout();
        }
      } catch (error) {
        console.error('Error loading token:', error);
        await logout();
      } finally {
        setIsLoading(false);
      }
    };
    loadToken();
  }, []);

  const login = async (accessToken: string, refreshToken: string, userData?: any) => {
    setToken(accessToken);
    loadUserData(accessToken);
    saveTokens(accessToken, refreshToken);
  };

  const logout = useCallback(async () => {
    setToken(null);
    removeTokens();
    navigate(routes.welcome);
  }, [navigate]);

  const handleRefreshToken = async () => {
    try {
      const savedRefreshToken = getRefreshToken();
      if (!savedRefreshToken) throw new Error('No refresh token found');

      const result = await refreshAccessToken(savedRefreshToken);
      if (!result) return;
      setToken(result.accessToken);
      loadUserData(result.accessToken);
      saveTokens(result.accessToken, result.refreshToken);
    } catch (error) {
      console.error('Error refreshing token:', error);
      await logout();
    }
  };

  useEffect(() => {
    const handleExpiredSession = () => {
      if (!getAccessToken() && !getRefreshToken()) return;
      logout();
      enqueueSnackbar('Sesión caducada, por favor, inicia sesión nuevamente.', {
        variant: 'warning',
      });
    };
    appEvents.addEventListener(EXPIRED_SESSION_EVENT, handleExpiredSession);
    return () => {
      appEvents.removeEventListener(EXPIRED_SESSION_EVENT, handleExpiredSession);
    };
  }, [logout]);

  return (
    <AuthContext.Provider value={{ token, isLoading, login, logout, refreshAccessToken: handleRefreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
